/**
 * IRL Toolbox — sidebar pattern mirroring the FDD Toolbox. Two tiles:
 *
 *   📋 IRL Review     — upload + column-mapping + saved-IRL review +
 *                        folder allowlist for the IRL matcher.
 *                        (Default tile when the tab opens.)
 *
 *   ✨ IRL Generator  — the sector-template-driven IRL generator (form
 *                        + entities table + checkbox review + Save).
 *
 * After the user saves a generated IRL we auto-flip back to Review and
 * bump a refresh counter so the panel re-mounts and re-reads from the
 * store. The two panels intentionally don't share state — both read from
 * the same `irlStore`, so the round-trip stays simple.
 *
 * This is a pure-presentation refactor: no behavior in either underlying
 * panel changed.
 */

import React, { useState } from "react";
import IRLReviewPanel from "./IRLReviewPanel";
import IRLGeneratorPanel from "./IRLGeneratorPanel";

type Props = {
  projectId: string;
};

type ToolId = "review" | "generator";

const TOOLS: { id: ToolId; label: string; icon: string; description: string }[] = [
  {
    id: "review",
    label: "IRL Review",
    icon: "📋",
    description: "Upload, normalize, and review the IRL list",
  },
  {
    id: "generator",
    label: "IRL Generator",
    icon: "✨",
    description: "Generate a sector-template IRL from a deal-setup form",
  },
];

const IRLToolbox: React.FC<Props> = ({ projectId }) => {
  const [activeTool, setActiveTool] = useState<ToolId>("review");
  // Bump this on Save inside the generator — Review re-mounts and re-reads
  // the freshly-saved IRL from `irlStore`.
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);

  const tool = TOOLS.find((t) => t.id === activeTool) ?? TOOLS[0];

  const handleGeneratorSaved = () => {
    setReviewRefreshKey((k) => k + 1);
    setActiveTool("review");
  };

  const handleGeneratorCancel = () => {
    setActiveTool("review");
  };

  return (
    <div style={{ marginTop: 16, display: "flex", gap: 16, alignItems: "stretch" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: 180,
          flexShrink: 0,
          border: "1px solid #c1e2b3",
          borderRadius: 12,
          background: "#f4fbf1",
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div style={{ fontWeight: 700, color: "#2f6b2f", padding: "4px 6px", fontSize: 13 }}>
          IRL Tools
        </div>
        {TOOLS.map((t) => {
          const isActive = t.id === activeTool;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTool(t.id)}
              style={{
                background: isActive ? "#6aa84f" : "#ffffff",
                color: isActive ? "#fff" : "#2f6b2f",
                border: `1px solid ${isActive ? "#6aa84f" : "#c1e2b3"}`,
                borderRadius: 8,
                padding: "10px 12px",
                cursor: "pointer",
                textAlign: "left",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          );
        })}
      </aside>

      {/* Main area */}
      <main
        style={{
          flex: 1,
          border: "1px solid #c1e2b3",
          borderRadius: 12,
          background: "#ffffff",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* Tool header (subtle — each panel keeps its own larger header). */}
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          <span style={{ fontWeight: 600, color: "#2f6b2f" }}>{tool.icon} {tool.label}</span>
          <span style={{ marginLeft: 6 }}>— {tool.description}</span>
        </div>

        {activeTool === "review" ? (
          // `key` forces a remount after a generator save so the review panel
          // re-reads the freshly-saved IRL from localStorage.
          <IRLReviewPanel key={reviewRefreshKey} projectId={projectId} />
        ) : (
          <IRLGeneratorPanel
            projectId={projectId}
            onSaved={handleGeneratorSaved}
            onCancel={handleGeneratorCancel}
          />
        )}
      </main>
    </div>
  );
};

export default IRLToolbox;
